"use client";

import { GradientCardProfileDetailRow } from "@/components/features/matter/information/GradientCardProfileDetailRow";
import {
  Badge,
  Button,
  ContactCallSplitButton,
  Typography,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveContactQueryOptions } from "@/lib/data/react-query";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Contact, Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import {
  formatAddress,
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { getContactHeroTypeIcon } from "@/lib/utils/iconUtils";
import {
  MATTER_CLIENT_HERO_GRADIENT_PANEL_CLASS,
  MATTER_DEFENDANT_HERO_GRADIENT_PANEL_CLASS,
  MATTER_PRIMARY_CONTACT_HERO_GRADIENT_PANEL_CLASS,
  MATTER_PROVIDER_HERO_GRADIENT_PANEL_CLASS,
  type MatterContactHeroTone,
} from "@/lib/utils/matters/matter-contact-connection-hero-tone";
import { useQuery } from "@tanstack/react-query";
import { startCase } from "lodash";
import {
  ChevronDown,
  ChevronRight,
  Mail,
  MapPin,
  MessageSquare,
  Pencil,
  Phone,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

/** Re-export gradient tokens from `matter-contact-connection-hero-tone` for backwards compatibility. */
export {
  MATTER_CLIENT_HERO_GRADIENT_PANEL_CLASS,
  MATTER_DEFENDANT_HERO_GRADIENT_PANEL_CLASS,
  MATTER_PRIMARY_CONTACT_HERO_GRADIENT_PANEL_CLASS,
  MATTER_PROVIDER_HERO_GRADIENT_PANEL_CLASS,
};

const HERO_DETAILS_HIDDEN_STORAGE_KEY_PREFIX =
  "matter-client-hero-details-hidden";

type HeroContact = Contact;

function pickPrimaryPhone(contact: HeroContact) {
  const phones = Array.isArray(contact.phones) ? contact.phones : [];
  return phones.find((p) => p.isPrimary) ?? phones.at(0);
}

function pickPrimaryEmail(contact: HeroContact) {
  const emails = Array.isArray(contact.emails) ? contact.emails : [];
  return emails.find((e) => e.isPrimary) ?? emails.at(0);
}

function pickPrimaryAddress(contact: HeroContact) {
  const addresses = Array.isArray(contact.addresses) ? contact.addresses : [];
  return addresses.find((a) => a.isPrimary) ?? addresses.at(0);
}

export interface MatterClientHeroPreviewCardProps {
  contact: HeroContact;
  matter?: Matter;
  /** Shown in the hero header (e.g. `Client`, `Primary Contact`). */
  slotLabel: string;
  matterId?: string;
  onEdit?: () => void;
  onDelete?: (contact: Contact) => void;
  disableDelete?: boolean;
  className?: string;
  tone?: MatterContactHeroTone;
  extraChips?: ReactNode;
  /** When true, hides contact type / subtype badges (e.g. defendant card shows role via `extraChips` only). */
  hideTypeChips?: boolean;
  editLoading?: boolean;
  /** Optional test id for the header edit (pencil) button. */
  editButtonTestId?: string;
}

/**
 * Client profile hero from Matter → Client information, reused where a full
 * client card is needed without a saved `MatterContactConnection` (e.g. create matter).
 */
export function MatterClientHeroPreviewCard({
  contact,
  matter,
  slotLabel,
  matterId,
  onEdit,
  onDelete,
  disableDelete,
  className,
  tone = "gray",
  extraChips,
  hideTypeChips = false,
  editLoading = false,
  editButtonTestId,
}: MatterClientHeroPreviewCardProps) {
  const { hasActionPermission } = useUser();
  const { openCommunicationModal } = useGlobalModal();
  const detailsHiddenStorageKey = useMemo(
    () =>
      [
        HERO_DETAILS_HIDDEN_STORAGE_KEY_PREFIX,
        matter?.id ?? matterId ?? "no-matter",
        slotLabel.trim().toLowerCase().replace(/\s+/g, "-"),
        contact.id || "no-contact",
      ].join(":"),
    [contact.id, matter?.id, matterId, slotLabel],
  );
  const [detailsHidden, setDetailsHidden] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    const storedValue = window.localStorage.getItem(detailsHiddenStorageKey);
    if (storedValue == null) {
      return true;
    }
    return storedValue === "true";
  });
  const { data: fullContactData } = useQuery(
    retrieveContactQueryOptions(contact.id),
  );
  const resolvedContact = fullContactData?.data ?? contact;

  const canViewContactCommunications = true;
  const canEditContact = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );

  const primaryPhone = pickPrimaryPhone(resolvedContact);
  const primaryEmail = pickPrimaryEmail(resolvedContact);
  const primaryAddress = pickPrimaryAddress(resolvedContact);

  const formattedContactName = formatContactsName(resolvedContact);
  const displayName =
    (typeof formattedContactName === "string" && formattedContactName) ||
    (typeof resolvedContact.organizationName === "string" &&
      resolvedContact.organizationName.trim()) ||
    (typeof resolvedContact.groupName === "string" &&
      resolvedContact.groupName.trim()) ||
    "—";

  const primaryPhoneNumber = primaryPhone?.number;
  const formattedPhone = primaryPhone
    ? formatLocalOrIntlPhoneNumber(primaryPhone)
    : "";

  const formattedAddress = primaryAddress
    ? formatAddress({
        street1: primaryAddress.street1 ?? undefined,
        street2: primaryAddress.street2 ?? undefined,
        city: primaryAddress.city ?? undefined,
        state: primaryAddress.state ?? "",
        zipCode: primaryAddress.zipCode ?? undefined,
        country:
          primaryAddress.country === "United States"
            ? undefined
            : (primaryAddress.country ?? undefined),
      })
    : "";

  const canCall = Boolean(primaryPhoneNumber);
  const canText = canCall && canViewContactCommunications;

  const contactSubTypes =
    resolvedContact.type === "individual"
      ? resolvedContact.individualSubTypes
      : resolvedContact.type === "organization"
        ? resolvedContact.organizationSubTypes
        : undefined;

  const subtypeLine = contactSubTypes?.map((s) => startCase(s)) ?? [];

  const waterLetter = displayName.trim().charAt(0) || "C";

  const resolvedMatterId = matter?.id ?? matterId;
  const HeroTypeIcon = getContactHeroTypeIcon(
    resolvedContact.type ?? undefined,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const storedValue = window.localStorage.getItem(detailsHiddenStorageKey);
    if (storedValue == null) {
      setDetailsHidden(true);
      return;
    }
    setDetailsHidden(storedValue === "true");
  }, [detailsHiddenStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(detailsHiddenStorageKey, String(detailsHidden));
  }, [detailsHidden, detailsHiddenStorageKey]);

  return (
    <div className={cn("relative w-full min-w-0", className)}>
      {(onDelete && !disableDelete) || (onEdit && canEditContact) ? (
        <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
          {onEdit && canEditContact ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full text-white/80 hover:bg-white/12 hover:text-white"
              disabled={editLoading}
              onClick={onEdit}
              data-testid={editButtonTestId}
            >
              {editLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
            </Button>
          ) : null}
          {onDelete && !disableDelete ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full text-white/80 hover:bg-white/12 hover:text-white"
              onClick={() => {
                if ("firmId" in contact) {
                  onDelete(contact);
                }
              }}
              data-testid="contact-preview-chip-delete"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ) : null}
      <div
        className={cn(
          "flex min-h-[240px] w-full min-w-0 flex-col",
          tone === "gray"
            ? MATTER_PRIMARY_CONTACT_HERO_GRADIENT_PANEL_CLASS
            : tone === "provider"
              ? MATTER_PROVIDER_HERO_GRADIENT_PANEL_CLASS
              : tone === "blue"
                ? MATTER_DEFENDANT_HERO_GRADIENT_PANEL_CLASS
                : MATTER_CLIENT_HERO_GRADIENT_PANEL_CLASS,
        )}
      >
        <div className="pointer-events-none absolute right-[-16px] bottom-[-34px] z-0 text-[9rem] font-semibold text-white/6 select-none">
          {waterLetter.toUpperCase()}
        </div>
        {/*
          Keep the expandable details block outside this inner flex column.
          Otherwise margin-top:auto on the action row collapses when details
          open (no free space), and Text/Call jump upward.
        */}
        <div className="relative z-10 flex min-h-[240px] min-w-0 shrink-0 flex-col">
          <div className="mb-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/12">
                <HeroTypeIcon className="h-7 w-7 text-white" />
              </div>
              <Typography className="pr-12 text-lg leading-tight font-semibold text-white/90">
                {slotLabel}
              </Typography>
            </div>

            <div className="space-y-2">
              <Typography
                data-testid="contact-preview-chip-name"
                className="truncate text-lg leading-snug font-semibold tracking-tight text-white sm:text-xl"
              >
                {displayName}
              </Typography>
              {(!hideTypeChips || extraChips) && (
                <div className="flex flex-wrap gap-2">
                  {!hideTypeChips && (
                    <Badge
                      variant="outline"
                      className="border-white/20 bg-white/5 text-xs text-white/70"
                    >
                      {startCase(resolvedContact.type ?? "")}
                    </Badge>
                  )}
                  {!hideTypeChips &&
                    subtypeLine.map((subtype) => (
                      <Badge
                        key={subtype}
                        variant="outline"
                        className="border-white/20 bg-white/5 text-xs text-white/70"
                      >
                        {subtype}
                      </Badge>
                    ))}
                  {extraChips}
                </div>
              )}
              <div className="flex min-h-[1.75rem] items-end">
                {formattedPhone ? (
                  <Typography className="text-sm text-white/90 sm:text-base">
                    {formattedPhone}
                  </Typography>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-auto flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-white/20 bg-white/8 text-white hover:bg-white/12 hover:text-white"
              onClick={() => {
                if (!canText) {
                  return;
                }
                openCommunicationModal({
                  contactId: resolvedContact.id,
                  matterId: resolvedMatterId,
                });
              }}
              disabled={!canText}
            >
              <MessageSquare className="h-4 w-4" />
              Text
            </Button>
            <ContactCallSplitButton
              contactId={resolvedContact.id}
              phones={resolvedContact.phones}
              variant="outline"
              size="sm"
              className="border-white/20 bg-white/8 text-white hover:bg-white/12 hover:text-white"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto px-2 text-white/70 hover:bg-transparent hover:text-white"
              onClick={() => {
                setDetailsHidden((current) => !current);
              }}
            >
              {detailsHidden ? "Details" : "Hide"}
              {detailsHidden ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div
          className={cn(
            "relative z-10 overflow-hidden transition-[max-height,opacity,margin-top] duration-200 ease-out",
            detailsHidden
              ? "mt-0 max-h-0 opacity-0"
              : "mt-5 max-h-80 opacity-100",
          )}
          aria-hidden={detailsHidden}
        >
          <div className="relative z-10 space-y-4 rounded-2xl border border-white/20 bg-white/12 p-4 shadow-sm backdrop-blur-md">
            <GradientCardProfileDetailRow
              icon={Phone}
              label="Phone"
              value={formattedPhone}
              onCopy={
                primaryPhoneNumber
                  ? () =>
                      void copyToClipboard(primaryPhoneNumber, "Phone number")
                  : undefined
              }
            />
            <GradientCardProfileDetailRow
              icon={Mail}
              label="Email"
              value={primaryEmail?.address ? primaryEmail.address : undefined}
              onCopy={
                primaryEmail?.address
                  ? () =>
                      void copyToClipboard(
                        primaryEmail.address ?? "",
                        "Email address",
                      )
                  : undefined
              }
            />
            <GradientCardProfileDetailRow
              icon={MapPin}
              label="Address"
              value={formattedAddress}
              onCopy={
                formattedAddress
                  ? () => void copyToClipboard(formattedAddress, "Address")
                  : undefined
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
